import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';

export interface VitaBeneficiario {
  id: string;
  usuarioId?: number;
  paisCodigo: string;
  variante: string;
  varianteLabel: string;
  tipoId: string;
  numeroId: string;
  nombre: string;
  apellidos: string | null;
  email: string | null;
  telefono: string | null;
  datosBancarios: Record<string, any>;
  bancoNombre: string;
  cuentaMask: string;
  vitaId: string | null;
  vitaEstado: 'ok' | 'banned';
  vitaChequeadoAt: string | null;
  activo: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface VitaBeneficiarioCreateRequest {
  paisCodigo: string;
  variante: string;
  varianteLabel?: string;
  tipoId: string;
  numeroId: string;
  nombre: string;
  apellidos?: string;
  email?: string;
  telefono?: string;
  datosBancarios: Record<string, any>;
  bancoNombre?: string;
  cuentaMask?: string;
  validateType?: string;
  validateValue?: string;
}

export type VitaRoutingValidationStatus =
  | 'valid'
  | 'not_registered'
  | 'bank_mismatch'
  | 'invalid_format'
  /** Cuenta bancaria clásica: Vita no valida existencia, no hay nada que pintar. */
  | 'not_applicable'
  | 'unknown';

export interface VitaRoutingValidationResult {
  status: VitaRoutingValidationStatus;
  bank?: string;
  ownerName?: string;
  message: string;
}

/** Campo del schema de Vita faltante (subset, ya filtrado/visible por backend). */
export interface VitaFaltanteField {
  key: string;
  name: string;
  type: string;
  options?: { value: string | number; label: string }[];
  min?: number;
  max?: number;
  when?: Record<string, string>;
}

export interface VitaCompletitudResult {
  valid: boolean;
  variante: string;
  rulesDisponibles?: boolean;
  faltantes: VitaFaltanteField[];
}

@Injectable({
  providedIn: 'root',
})
export class VitaBeneficiarioService {
  private baseUrl = `${environment.apiNest}vita-wallet/beneficiarios`;

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  private async getHeaders(): Promise<HttpHeaders> {
    // idToken (NO accessToken): backend valida `tokenUse: 'id'` — espejo del
    // ApiRestInterceptor. accessToken → 401 "Sesión expirada" en bucle.
    let session = await this.cognitoService.getSession();
    let token = session?.idToken?.jwtToken;

    if (token && this.isJwtNearExpiry(token, 60)) {
      if (typeof (this.cognitoService as any).clearCache === 'function') {
        (this.cognitoService as any).clearCache();
      }
      session = await this.cognitoService.getSession();
      token = session?.idToken?.jwtToken;
    }
    if (!token) throw new Error('No se pudo obtener el token de sesión');

    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${token.trim()}`);
  }

  private isJwtNearExpiry(jwt: string, thresholdSec: number): boolean {
    try {
      const parts = jwt.split('.');
      if (parts.length !== 3) return false;
      const payload = JSON.parse(
        atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
      );
      if (!payload?.exp) return false;
      const nowSec = Math.floor(Date.now() / 1000);
      return payload.exp - nowSec < thresholdSec;
    } catch {
      return false;
    }
  }

  private handleError(error: HttpErrorResponse) {
    const msg =
      error.error?.message ||
      (typeof error.error === 'string' ? error.error : '') ||
      `Error del servidor: ${error.status}`;
    return throwError(() => msg);
  }

  list(pais?: string, q?: string): Observable<VitaBeneficiario[]> {
    let params = new HttpParams();
    if (pais) params = params.set('pais', pais);
    if (q) params = params.set('q', q);
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<VitaBeneficiario[]>(this.baseUrl, { headers, params }),
      ),
      catchError(this.handleError),
    );
  }

  getById(id: string): Observable<VitaBeneficiario> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<VitaBeneficiario>(`${this.baseUrl}/${id}`, { headers }),
      ),
      catchError(this.handleError),
    );
  }

  create(body: VitaBeneficiarioCreateRequest): Observable<VitaBeneficiario> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<VitaBeneficiario>(this.baseUrl, body, { headers }),
      ),
      catchError(this.handleError),
    );
  }

  /** Edita un beneficiario existente (re-valida + re-dedup). POST, no PATCH. */
  editar(
    id: string,
    body: VitaBeneficiarioCreateRequest,
  ): Observable<VitaBeneficiario> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<VitaBeneficiario>(`${this.baseUrl}/${id}/editar`, body, {
          headers,
        }),
      ),
      catchError(this.handleError),
    );
  }

  refreshVita(id: string): Observable<VitaBeneficiario> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<VitaBeneficiario>(
          `${this.baseUrl}/${id}/refresh-vita`,
          {},
          { headers },
        ),
      ),
      catchError(this.handleError),
    );
  }

  archive(id: string): Observable<{ archived: true }> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.delete<{ archived: true }>(`${this.baseUrl}/${id}`, {
          headers,
        }),
      ),
      catchError(this.handleError),
    );
  }

  /**
   * Valida que los datos guardados del beneficiario cumplan las reglas VIVAS
   * de su variante. Si `valid=false`, `faltantes` trae los campos a completar.
   */
  completitud(id: string): Observable<VitaCompletitudResult> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.get<VitaCompletitudResult>(
          `${this.baseUrl}/${id}/completitud`,
          { headers },
        ),
      ),
      catchError(this.handleError),
    );
  }

  /** Completa/actualiza los datos faltantes del beneficiario (merge por variante). */
  completar(
    id: string,
    datos: Record<string, any>,
  ): Observable<VitaCompletitudResult> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<VitaCompletitudResult>(
          `${this.baseUrl}/${id}/completar`,
          { datos },
          { headers },
        ),
      ),
      catchError(this.handleError),
    );
  }

  /**
   * Llama al endpoint on-blur del backend. Aplica a los rieles de LLAVE
   * (Bre-B, PIX, CLABE, IBAN, ABA, CVU/alias); para cuenta bancaria clásica
   * el backend responde `not_applicable`. Estado normalizado listo para UI.
   */
  validateRouting(body: {
    paisCodigo: string;
    validateType: string;
    validateValue: string;
    /** Variante de Vita: distingue riel de llave (`cobreb`) de cuenta (`co`). */
    variante?: string;
    /** Solo los campos de ruteo, para que el backend aplique el mismo criterio. */
    datosBancarios?: Record<string, any>;
  }): Observable<VitaRoutingValidationResult> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) =>
        this.http.post<VitaRoutingValidationResult>(
          `${this.baseUrl}/validate-routing`,
          body,
          { headers },
        ),
      ),
      catchError(this.handleError),
    );
  }
}
